import * as d3 from "d3";
import React from "react";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";

export interface IGroupedData {
  label: string;
  values: number[];
}
export interface ILineData {
  index: number;
  value: number;
}
interface Props {
  data: IGroupedData[];
  CurrencySign: string,
  PercentageSign: string;
  legendsHOB: string[];
  colorsForBars: string[];
  average: number[];
  YoYGrowth: number[];
  lineData: [number, number][];
  Height: number;
  MarginLeft: number
}

interface BarProps {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  onMouseEnter: (e: MouseEvent<SVGPathElement>) => void;
  onMouseLeave: () => void;
}

interface Tooltip {
  x: number;
  y: number;
  index: number;
  barIndex: number;

  stackIndex: number;
}

function Bar({
  x,
  y,
  width,
  height,
  color,
  onMouseEnter,
  onMouseLeave,
}: BarProps) {
  const radius = height === 0 ? 0 : 3.0;
  return (
    <path
      d={`
      m${x},${y + radius}
      a${radius},${radius} 0 0 1 ${radius},${-radius}
      h${width - 2 * radius}
      a${radius},${radius} 0 0 1 ${radius},${radius}
      v${height - radius}
      h-${width}
      z
    `}
      fill={color}
      onMouseEnter={(event) => onMouseEnter(event)}
      onMouseLeave={onMouseLeave}
    />
  );
}
export function BarChartWithLine({
  data,
  PercentageSign,
  CurrencySign,
  legendsHOB,
  colorsForBars,
  average,
  YoYGrowth,
  Height,
  MarginLeft,
  lineData
}: Props) {
  //  useEffect(() => {}, []);
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const axisBottomRef = useRef<SVGGElement>(null);
  const axisLeftRef = useRef<SVGGElement>(null);

  const chartRef = useRef(null);
  const svgContainer: any = d3.select(chartRef.current).node();
  var BottomDivWidth = svgContainer?.getBoundingClientRect()?.width || 800;

  const margin = { top: 30, right: 0, bottom: 20, left: MarginLeft };
  let width = BottomDivWidth - 15 - margin.left - margin.right || 0;
  const height = Height - margin.top - margin.bottom;

  const labels = data.map(({ label }) => label);
  var sublabels: any = [];
  if (data[0])
    sublabels = Object.keys(data[0].values);
  const scaleX = d3.scaleBand().domain(labels).range([0, width]).padding(0.2);
  var maxYscale = 0;
  const [refresh, setrefresh] = useState(false);
  data.map((d) => {
    var localMax = 0;
    d.values.map((v) => {
      localMax = v;

      if (localMax > maxYscale) maxYscale = localMax;
    });
  });
  lineData.map((d) => {
    var localMax = d[1];

    if (localMax > maxYscale) maxYscale = localMax;
  });
  maxYscale = maxYscale + (maxYscale ) / 12;
  maxYscale = maxYscale + 10;
  const scaleY = d3.scaleLinear().domain([0, maxYscale]).range([height, 0]);

  const subscaleX = d3
    .scaleBand()
    .domain(sublabels)
    .range([0, scaleX.bandwidth()])
    .padding(0.1);
  const linePath =
    d3
      .line()
      .x(
        (d) =>
          (scaleX(labels[d[0] - 1]) || 0) + margin.left + scaleX.bandwidth() / 2
      )
      .y((d) => scaleY(d[1]) + margin.top)(lineData) || undefined;
  useEffect(() => {
    const svgContainer: any = d3.select(chartRef.current).node();
    var BottomDivWidth = svgContainer?.getBoundingClientRect()?.width || 800;

    width = BottomDivWidth - 15 - margin.left - margin.right || 0;

    setrefresh(!refresh);
  }, [width]);

  useEffect(() => {
    width = BottomDivWidth - 15 - margin.left - margin.right || 0;
    if (axisBottomRef.current) {
      d3.select(axisBottomRef.current).call(d3.axisBottom(scaleX));
    }

    if (axisLeftRef.current) {
      d3.select(axisLeftRef.current).call(
        d3
          .axisLeft(scaleY)
          .tickValues(
            d3.range(0, maxYscale, Math.round(maxYscale / 5))
          )
          .tickFormat(function (d, i) {
            return CurrencySign + d?.toLocaleString() + PercentageSign;
          })
      );
    }
  }, [scaleX, scaleY]);

  return (
    <div className="" ref={chartRef}>
      <svg
        width={width + margin.left + margin.right}
        height={height + margin.top + margin.bottom}
      >
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <g ref={axisBottomRef} transform={`translate(0, ${height})`} />
          <g ref={axisLeftRef} />
          {data.map(({ label, values }, groupIndex) => (
            <g
              key={`rect-group-${groupIndex}`}
              transform={`translate(${scaleX(label)}, 0)`}
            >
              {values.map((value, barIndex) => (
                <>
                  <Bar
                    key={`rect-${barIndex}`}
                    x={subscaleX(String(barIndex)) || 0}
                    y={scaleY(value)}
                    width={subscaleX.bandwidth()}
                    height={height - scaleY(value)}
                    color={colorsForBars[barIndex]}
                    onMouseEnter={(event) => {
                      setTooltip({
                        x: event.clientX,
                        y: event.clientY,
                        index: groupIndex,
                        stackIndex: 0,
                        barIndex: barIndex,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
             {  lineData.length> 0 &&   <circle
                    key={"cor" + groupIndex}
                    fill={colorsForBars[0]}
                    stroke={"1"}
                    cx={scaleX.bandwidth() / 2}
                    //                cx={ ((getX(labels[(lineData[groupIndex][0]-1)])|| 0) ||0 - ( groupIndex+1 * subscaleX.bandwidth() * scaleX.bandwidth()))}
                    cy={scaleY(lineData[groupIndex][1])}
                    r={3}
                    onMouseEnter={(event) => {
                      setTooltip({
                        x: event.clientX,
                        y: event.clientY,
                        index: groupIndex,
                        
                        stackIndex: -1,
                        barIndex: barIndex,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />}

                </>
              ))}
            </g>
          ))}
        </g>
        <path
          key={`path-rect-`}
          className="c-linechart-line__path"
          d={linePath}
          strokeLinecap="round"
          strokeWidth="3"
          stroke={colorsForBars[0]}
          fill="none"
        />
      </svg>
      {
        <div className="Legend-main3">
          {legendsHOB &&
            legendsHOB.length > 0 &&
            legendsHOB.map((Item: any, index: any) => (
              <div className="leg-item">
                <span className={"clegend-" + index}></span>
                <p>{Item}</p>
              </div>
            ))}
        </div>
      }
      {tooltip !== null ? (
        <div className="tooltip" style={{ top: tooltip.y, left: tooltip.x }}>
          <span className="tooltip__title">{labels[tooltip.index]}</span>
          <table className="tooltip__table">
            <tbody>
              {" "}
              {legendsHOB && legendsHOB.map(
                  (Item: any, index: any) => (
                    <>
                      <tr key={"rowModel444" + index}>
                        <td
                          className={
                            index == tooltip.barIndex
                              ? "tooltip-Highlight"
                              : ""
                          }
                        >
                          {Item}
                        </td>
                        <td
                          className={
                            index == tooltip.barIndex
                              ? "tooltip-Highlight"
                              : ""
                          }
                        >
                          {PercentageSign}
                          {data[tooltip.index].values[tooltip.barIndex]?.toLocaleString()}
                        </td>
                      </tr>
                    </>
                  )
                )}
              {lineData.length> 0 && <tr key={`div-rectrrrr`+tooltip.stackIndex}>
                <td
                  className={
                    -1 == tooltip.stackIndex ? "tooltip-Highlight" : ""
                  }
                >
                  PAID OUT %
                </td>
                <td
                  className={
                    -1 == tooltip.stackIndex ? "tooltip-Highlight" : ""
                  }
                >
                
                  {lineData[tooltip.index][1].toLocaleString()+" %"}
                </td>
              </tr> }
              {YoYGrowth.length > 0 && YoYGrowth[tooltip.index] != null && (
                <tr>
                  <td>YoY Growth</td>
                  <td>{YoYGrowth[tooltip.index].toLocaleString() + "%"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
